import { canStackKinds, normalizePartKind } from "@/home/part-kind";
import { findSocketSnap } from "@/home/sockets";
import {
  clampPlaceScale,
  kindQuotaRemaining,
  type HomePartDefinition,
  type HomePlacement,
  type HomeRoomData,
} from "@/home/storage";

export function effectivePlacementDepthBias(
  part: HomePartDefinition,
  placement: Pick<HomePlacement, "depthBias">,
): number {
  if (placement.depthBias !== undefined) return placement.depthBias;
  return part.depthBias ?? 0;
}

export function effectivePlacementPlaceScale(
  part: HomePartDefinition,
  placement: Pick<HomePlacement, "placeScale">,
): number {
  if (placement.placeScale !== undefined) {
    return clampPlaceScale(placement.placeScale);
  }
  return clampPlaceScale(part.placeScale ?? 1);
}

export type GridRect = { x: number; y: number; w: number; h: number };

export function partById(
  data: HomeRoomData,
  partId: string,
): HomePartDefinition | undefined {
  return data.parts.find((p) => p.id === partId);
}

export function partFootprint(part: HomePartDefinition): { w: number; h: number } {
  return {
    w: part.footprintW || part.cellsW,
    h: part.footprintH || part.cellsH,
  };
}

export function placementRect(
  placement: HomePlacement,
  part: HomePartDefinition,
): GridRect {
  const fp = partFootprint(part);
  return {
    x: placement.gridX,
    y: placement.gridY,
    w: fp.w,
    h: fp.h,
  };
}

/** Authoring — match visible sprite grid (selection outline, ghost). */
export function placementCellsRect(
  placement: HomePlacement,
  part: HomePartDefinition,
): GridRect {
  return {
    x: placement.gridX,
    y: placement.gridY,
    w: part.cellsW,
    h: part.cellsH,
  };
}

export function rectsOverlap(a: GridRect, b: GridRect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export function canPlaceAt(
  data: HomeRoomData,
  partId: string,
  gridX: number,
  gridY: number,
  ignorePlacementId?: string,
): boolean {
  const part = partById(data, partId);
  if (!part) return false;

  const kind = normalizePartKind(part.kind);
  if (kind === "backdrop" || kind === "shell") {
    return true;
  }

  // New placements only — moves keep quota.
  if (!ignorePlacementId && kindQuotaRemaining(data, kind) <= 0) {
    return false;
  }

  const candidate: GridRect = {
    x: gridX,
    y: gridY,
    w: part.cellsW,
    h: part.cellsH,
  };
  if (
    candidate.x < 0 ||
    candidate.y < 0 ||
    candidate.x + candidate.w > data.gridCols ||
    candidate.y + candidate.h > data.gridRows
  ) {
    return false;
  }

  for (const existing of data.placements) {
    if (ignorePlacementId && existing.id === ignorePlacementId) continue;
    const existingPart = partById(data, existing.partId);
    if (!existingPart) continue;
    if (!rectsOverlap(candidate, placementCellsRect(existing, existingPart))) continue;
    if (!canStackKinds(kind, normalizePartKind(existingPart.kind))) {
      return false;
    }
  }
  return true;
}

function cellInRect(col: number, row: number, rect: GridRect): boolean {
  return (
    col >= rect.x &&
    col < rect.x + rect.w &&
    row >= rect.y &&
    row < rect.y + rect.h
  );
}

/** Play-mode collision rect — house/solid uses full sprite cells so you cannot cut through art. */
export function playModeBlockRect(
  placement: HomePlacement,
  part: HomePartDefinition,
): GridRect | null {
  if (part.passage) return null;
  const kind = normalizePartKind(part.kind);
  if (kind === "solid") {
    return {
      x: placement.gridX,
      y: placement.gridY,
      w: part.cellsW,
      h: part.cellsH,
    };
  }
  if (!part.occupies || part.walkable) return null;
  return placementRect(placement, part);
}

/** Play-mode: cell blocked by house, monster, etc. (not passage / walkable). */
export function isCellBlocked(data: HomeRoomData, col: number, row: number): boolean {
  for (const placement of data.placements) {
    const part = partById(data, placement.partId);
    if (!part) continue;
    const rect = playModeBlockRect(placement, part);
    if (!rect) continue;
    if (cellInRect(col, row, rect)) {
      return true;
    }
  }
  return false;
}

export function isCellWalkable(data: HomeRoomData, col: number, row: number): boolean {
  if (col < 0 || row < 0 || col >= data.gridCols || row >= data.gridRows) {
    return false;
  }
  return !isCellBlocked(data, col, row);
}

const WALK_NEIGHBORS = [
  [0, 1],
  [0, -1],
  [1, 0],
  [-1, 0],
] as const;

/** Grid path avoiding blocked cells (4-way). Empty array = already at goal. Null = unreachable. */
export function findWalkPath(
  data: HomeRoomData,
  fromCol: number,
  fromRow: number,
  toCol: number,
  toRow: number,
): { col: number; row: number }[] | null {
  if (!isCellWalkable(data, toCol, toRow)) return null;
  if (fromCol === toCol && fromRow === toRow) return [];

  const startKey = `${fromCol},${fromRow}`;
  const goalKey = `${toCol},${toRow}`;
  const queue: { col: number; row: number }[] = [{ col: fromCol, row: fromRow }];
  const cameFrom = new Map<string, string | null>([[startKey, null]]);

  while (queue.length > 0) {
    const cur = queue.shift()!;
    const curKey = `${cur.col},${cur.row}`;
    if (curKey === goalKey) break;

    for (const [dc, dr] of WALK_NEIGHBORS) {
      const ncol = cur.col + dc;
      const nrow = cur.row + dr;
      if (!isCellWalkable(data, ncol, nrow)) continue;
      const nextKey = `${ncol},${nrow}`;
      if (cameFrom.has(nextKey)) continue;
      cameFrom.set(nextKey, curKey);
      queue.push({ col: ncol, row: nrow });
    }
  }

  if (!cameFrom.has(goalKey)) return null;

  const steps: { col: number; row: number }[] = [];
  let key: string | null = goalKey;
  while (key && key !== startKey) {
    const [col, row] = key.split(",").map((n) => Number.parseInt(n, 10));
    steps.push({ col, row });
    key = cameFrom.get(key) ?? null;
  }
  steps.reverse();
  return steps;
}

/** True when cell has a walkable platform (for jump/step cue). */
export function cellHasPlatform(data: HomeRoomData, col: number, row: number): boolean {
  for (const placement of data.placements) {
    const part = partById(data, placement.partId);
    if (!part || part.kind !== "platform") continue;
    const rect = placementRect(placement, part);
    if (
      col >= rect.x &&
      col < rect.x + rect.w &&
      row >= rect.y &&
      row < rect.y + rect.h
    ) {
      return true;
    }
  }
  return false;
}

export const PLATFORM_JUMP_RANGE = 3;

/** Resolve place cell — props snap to solid sockets when near. */
export function resolvePlaceCell(
  data: HomeRoomData,
  partId: string,
  col: number,
  row: number,
): { col: number; row: number; snapped: boolean } {
  const part = partById(data, partId);
  if (!part) return { col, row, snapped: false };
  const kind = normalizePartKind(part.kind);
  if (kind !== "prop" && kind !== "attack_dummy") {
    return { col, row, snapped: false };
  }
  const snap = findSocketSnap(data, partId, col, row, 1);
  if (!snap) return { col, row, snapped: false };
  return { col: snap.col, row: snap.row, snapped: true };
}

/** First grid cell where a new placement is allowed (row-major scan). */
export function findFirstPlaceCell(
  data: HomeRoomData,
  partId: string,
): { col: number; row: number } | null {
  const part = partById(data, partId);
  if (!part) return null;
  const kind = normalizePartKind(part.kind);
  if (kind === "backdrop" || kind === "shell") return { col: 0, row: 0 };
  const fp = partFootprint(part);
  if (fp.w > data.gridCols || fp.h > data.gridRows) return null;
  for (let row = 0; row <= data.gridRows - fp.h; row++) {
    for (let col = 0; col <= data.gridCols - fp.w; col++) {
      const snapped = resolvePlaceCell(data, partId, col, row);
      if (canPlaceAt(data, partId, snapped.col, snapped.row)) {
        return { col: snapped.col, row: snapped.row };
      }
    }
  }
  return null;
}
